import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, InputGroup, Stack, Table } from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import SearchIcon from '@rsuite/icons/Search';
import { IoMdPricetags } from 'react-icons/io';
import { FaEdit, FaImage } from 'react-icons/fa';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { ContentHeader } from '../../components/ContentHeader';
import DuvidasButton from '../../components/DuvidasButton';
import { CustomButton } from '../../components/CustomButton';
import { CustomInput } from '../../components/CustomInput';
import { CustomSelectPicker } from '../../components/CustomSelectPicker';
import { debounce } from '../../components/funcoes';
import EditarCadastrarPromocao from './EditarCadastrar';
import { CustomHeaderCell } from '../../components/CustomHeaderCell';
import { CustomCell } from '../../components/CustomCell';
import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import ImagemPromocao from './Imagem';
import { formatCurrencyBRL } from '../../utils/utils';
import { toastError, toastInfo } from '../../toasts';
import {
  tipoPromocaoOptions,
  tipoPromocaoBadgeStyle,
  tipoPromocaoBadgeFallback,
  tipoPromocaoIcon,
  tipoPromocaoIconFallback,
  getTipoPromocaoLabel,
} from './constants';
import { StatusBadge } from '../../components/ManagementCardGrid';
import * as S from './styles';

const { Column } = Table;

const statusOptions = [
  { label: 'Ativo', value: 1 },
  { label: 'Inativo', value: 0 },
];

const sortOptions = [
  { label: 'Mais recentes', value: 'recentes' },
  { label: 'Título (A-Z)', value: 'titulo_asc' },
  { label: 'Título (Z-A)', value: 'titulo_desc' },
  { label: 'Tipo de promoção', value: 'tipo' },
];

const Promocao = () => {
  const { id_empresa } = useAuth();
  const [loading, setLoading] = useState(true);
  const [produtos, setProdutos] = useState([]);
  const [bordas, setBordas] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [promocoes, setPromocoes] = useState([]);
  const [promocao, setPromocao] = useState(null);
  const [modalCreate, setModalCreate] = useState(false);
  const [modalImagem, setModalImagem] = useState(false);
  const [isBorderPromo, setIsBorderPromo] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState(null);
  const [ordenacao, setOrdenacao] = useState('recentes');
  const { theme } = useCustomTheme();

  const debounceSearch = debounce((valor = '') => {
    setBusca(valor.toLocaleLowerCase());
  }, 500);

  const promocoesFiltradas = useMemo(() => {
    let lista = promocoes;

    if (busca.trim() !== '') {
      lista = lista.filter((item) =>
        [item.Id, item.Titulo, item.Descricao]
          .filter(Boolean)
          .some((campo) => String(campo).toLocaleLowerCase().includes(busca)),
      );
    }

    if (filtroTipo) {
      lista = lista.filter((item) => item.Tipo === filtroTipo);
    }

    if (filtroStatus !== null && filtroStatus !== undefined) {
      lista = lista.filter((item) => !!item.Ativo === !!filtroStatus);
    }

    lista = [...lista];

    if (ordenacao === 'titulo_asc') {
      lista.sort((a, b) => (a.Titulo || '').localeCompare(b.Titulo || ''));
    } else if (ordenacao === 'titulo_desc') {
      lista.sort((a, b) => (b.Titulo || '').localeCompare(a.Titulo || ''));
    } else if (ordenacao === 'tipo') {
      lista.sort((a, b) => (a.Tipo || '').localeCompare(b.Tipo || ''));
    }

    return lista;
  }, [promocoes, busca, filtroTipo, filtroStatus, ordenacao]);

  const consultar = useCallback(
    async (
      consulta = ' p.Grupo IS NOT NULL AND g.Ativo = 1 AND p.Ativo = 1',
    ) => {
      consulta += ` AND p.TipoProduto != 'BORDA'`;
      await api
        .post('produto/listarPorGrupoGestor', {
          IdEmpresa: id_empresa,
          Consulta: consulta,
        })
        .then((res) => {
          const listProducts = res.data.map((produto) => {
            return {
              label: `${produto.Codigo}: ${produto.Descricao} ${produto.IsPizza ? '' : ` - ${formatCurrencyBRL(produto.PrecoVenda)}`}`,
              value: produto.Id,
            };
          });
          setProdutos(listProducts);
        })
        .catch((error) => console.log({ error }))
        .finally(() => setLoading(false));
    },
    [id_empresa],
  );

  const listarBordas = useCallback(async () => {
    await api
      .post('produto/listarPorGrupoGestor', {
        IdEmpresa: id_empresa,
        Consulta: " p.TipoProduto = 'BORDA' AND p.Ativo = 1",
      })
      .then((res) => {
        const listBorders = res.data.map((produto) => {
          return {
            label: `${produto.Codigo}: ${produto.Descricao} - ${formatCurrencyBRL(produto.PrecoVenda)}`,
            value: produto.Id,
          };
        });
        setBordas(listBorders);
      })
      .catch((error) => console.log({ error }))
      .finally(() => setLoading(false));
  }, [id_empresa]);

  const alterarStatus = async (obj, status) => {
    await api
      .patch(`promocao/alterar/${obj.Id}`, {
        Ativo: status,
      })
      .then((res) => {
        setPromocoes((state) =>
          state.map((e) => (e.Id !== res.data.Id ? e : res.data)),
        );
        toastInfo('Promoção alterada');
      })
      .catch((error) => {
        toastError(
          error?.response?.data?.Msg || 'Não foi possível alterar a promoção.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const listarTamanhos = useCallback(async () => {
    await api
      .get(`tamanho/listar_pizzas/${id_empresa}`)
      .then((res) => {
        const seen = new Set();
        const list = res.data
          .filter((tam) => {
            if (seen.has(tam.Descricao)) return false;
            seen.add(tam.Descricao);
            return true;
          })
          .map((tam) => ({
            label: tam.Descricao,
            value: tam.Id,
            descricao: tam.Descricao,
          }));
        setTamanhos(list);
      })
      .catch((error) => console.log({ error }));
  }, [id_empresa]);

  const listarPromocoes = useCallback(async () => {
    await api
      .get(`/promocao/listarGestor/${id_empresa}`)
      .then((response) => {
        setPromocoes(response.data);
      })
      .catch((error) => console.log(error));
  }, [id_empresa]);

  const abrirDados = (promocao) => {
    setPromocao(promocao);
    setModalCreate(true);
  };

  const inserirImagem = (promocao) => {
    setPromocao(promocao);
    setModalImagem(true);
  };

  useEffect(() => {
    listarPromocoes();
  }, [listarPromocoes]);

  useEffect(() => {
    consultar();
    listarTamanhos();
    if (isBorderPromo) {
      listarBordas();
    }
  }, [consultar, isBorderPromo, listarBordas, listarTamanhos]);

  return (
    <Container
      style={{
        padding: '10px',
        height: 'calc(100vh - 60px)',
        backgroundColor: theme.colors.background,
        overflow: 'hidden',
      }}
    >
      <ContentHeader
        title="Promoções"
        subtitle="Gerencie as promoções da sua empresa"
        icon={IoMdPricetags}
        thirdComponent={
          <Stack spacing={10}>
            <DuvidasButton tela="promocao" />
            <CustomButton
              appearance="primary"
              onClick={() => {
                setPromocao(null);
                setModalCreate(true);
              }}
            >
              <PlusIcon style={{ marginRight: 6 }} />
              Adicionar Promoção
            </CustomButton>
          </Stack>
        }
      />

      <S.FilterBar>
        <S.SearchBox>
          <InputGroup inside style={{ width: '100%' }}>
            <CustomInput
              placeholder="Buscar por código, título ou descrição..."
              onChange={debounceSearch}
            />
            <InputGroup.Addon>
              <SearchIcon color={theme.colors.textSecondary} />
            </InputGroup.Addon>
          </InputGroup>
        </S.SearchBox>

        <S.FilterField>
          <CustomSelectPicker
            placeholder="Tipo de promoção"
            data={tipoPromocaoOptions}
            value={filtroTipo}
            onChange={setFiltroTipo}
            searchable={false}
            style={{ width: '100%' }}
          />
        </S.FilterField>

        <S.FilterField>
          <CustomSelectPicker
            placeholder="Status"
            data={statusOptions}
            value={filtroStatus}
            onChange={setFiltroStatus}
            searchable={false}
            style={{ width: '100%' }}
          />
        </S.FilterField>

        <S.FilterField>
          <CustomSelectPicker
            placeholder="Ordenar"
            data={sortOptions}
            value={ordenacao}
            onChange={(valor) => setOrdenacao(valor || 'recentes')}
            searchable={false}
            cleanable={false}
            style={{ width: '100%' }}
          />
        </S.FilterField>
      </S.FilterBar>

      <Table
        height={window.innerHeight - 60 - 150}
        bordered={true}
        loading={loading}
        data={promocoesFiltradas}
        width={'100%'}
        style={{
          overflowY: 'auto',
          borderColor: theme.colors.backgroundSecondary,
          backgroundColor: theme.colors.backgroundSecondary,
          color: theme.colors.textSecondary,
        }}
        locale={{
          emptyMessage: 'Nenhuma promoção encontrada',
          loading: 'Carregando...',
        }}
      >
        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          align="center"
          width={60}
        >
          <CustomHeaderCell></CustomHeaderCell>
          <CustomCell dataKey="Id">
            {(rowData) => {
              const Icon =
                tipoPromocaoIcon[rowData.Tipo] || tipoPromocaoIconFallback;
              const { color } =
                tipoPromocaoBadgeStyle[rowData.Tipo] ||
                tipoPromocaoBadgeFallback;
              return (
                <S.RowAvatarWrapper>
                  <S.RowAvatar $bg={color}>
                    <Icon size={14} />
                  </S.RowAvatar>
                </S.RowAvatarWrapper>
              );
            }}
          </CustomCell>
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          align="center"
          fixed
        >
          <CustomHeaderCell>Código</CustomHeaderCell>
          <CustomCell dataKey="Id" />
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          flexGrow={3}
        >
          <CustomHeaderCell>Título</CustomHeaderCell>
          <CustomCell dataKey="Titulo" />
        </Column>
        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          flexGrow={4}
        >
          <CustomHeaderCell>Descrição</CustomHeaderCell>
          <CustomCell dataKey="Descricao" />
        </Column>
        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          align="center"
          flexGrow={3}
        >
          <CustomHeaderCell>Tipo de Promoção</CustomHeaderCell>
          <CustomCell dataKey="Tipo">
            {(rowData) => {
              const estilo =
                tipoPromocaoBadgeStyle[rowData.Tipo] ||
                tipoPromocaoBadgeFallback;
              return (
                <S.TypeBadge $bg={estilo.bg} $color={estilo.color}>
                  {getTipoPromocaoLabel(rowData.Tipo)}
                </S.TypeBadge>
              );
            }}
          </CustomCell>
        </Column>
        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          align="center"
          fixed="right"
        >
          <CustomHeaderCell>Ativo</CustomHeaderCell>

          <CustomCell dataKey="Ativo">
            {(rowData) => (
              <StatusBadge
                $active={!!rowData.Ativo}
                onClick={() => alterarStatus(rowData, !rowData.Ativo)}
                title={
                  rowData.Ativo ? 'Clique para desativar' : 'Clique para ativar'
                }
              >
                {rowData.Ativo ? 'Ativo' : 'Inativo'}
              </StatusBadge>
            )}
          </CustomCell>
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          fixed="right"
          flexGrow={2}
        >
          <CustomHeaderCell>Opções</CustomHeaderCell>

          <CustomCell>
            {(rowData) => (
              <S.OptionsCell>
                <CustomButton
                  color={theme.colors.info}
                  appearance="ghost"
                  size="xs"
                  onClick={() => abrirDados(rowData)}
                >
                  <FaEdit size={11} style={{ marginRight: 4 }} />
                  Editar
                </CustomButton>
                <CustomButton
                  color={theme.colors.loading}
                  appearance="primary"
                  size="xs"
                  onClick={() => inserirImagem(rowData)}
                >
                  <FaImage size={11} style={{ marginRight: 4 }} />
                  Imagem
                </CustomButton>
              </S.OptionsCell>
            )}
          </CustomCell>
        </Column>
      </Table>

      {modalCreate && (
        <EditarCadastrarPromocao
          open={modalCreate}
          close={() => setModalCreate(false)}
          promocao={promocao}
          setPromocao={setPromocao}
          setPromocoes={setPromocoes}
          produtos={produtos}
          bordas={bordas}
          tamanhos={tamanhos}
          setIsBorderPromo={setIsBorderPromo}
        />
      )}
      {modalImagem && (
        <ImagemPromocao
          modal={modalImagem}
          close={() => setModalImagem(false)}
          promocao={promocao}
        />
      )}
    </Container>
  );
};

export default Promocao;
